import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Mic, Languages } from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { ChannelIcon } from '../components/shared/ChannelIcon';
import { StatusBadge } from '../components/shared/StatusBadge';
import { MessageDetailSkeleton } from '../components/shared/Skeletons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── Language list ─────────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: 'af', name: 'Afrikaans' },
  { code: 'en', name: 'English' },
  { code: 'nr', name: 'isiNdebele' },
  { code: 'xh', name: 'isiXhosa' },
  { code: 'zu', name: 'isiZulu' },
  { code: 'nso', name: 'Sepedi' },
  { code: 'st', name: 'Sesotho' },
  { code: 'tn', name: 'Setswana' },
  { code: 'ss', name: 'siSwati' },
  { code: 've', name: 'Tshivenda' },
  { code: 'ts', name: 'Xitsonga' },
  { code: 'ar', name: 'Arabic' },
  { code: 'fr', name: 'French' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'sw', name: 'Swahili' },
  { code: 'other', name: 'Other (auto-detect)' },
];

// ─── Theme helpers ────────────────────────────────────────────────────────────

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 45%, 40%)`;
}

function getThemeColor(theme) {
  return stringToColor(theme);
}

function getMessageThemes(message) {
  if (Array.isArray(message.themes) && message.themes.length > 0) return message.themes;
  if (message.theme) return [message.theme];
  return ['uncategorized'];
}

// ─── Sentiment config ─────────────────────────────────────────────────────────

const sentimentConfig = {
  very_positive: { color: 'text-[#2e4d6e]',  bg: 'bg-[#395b81]/10 border border-[#395b81]/30', label: 'Very Positive' },
  positive:      { color: 'text-[#547a84]',  bg: 'bg-[#547a84]/10 border border-[#547a84]/30', label: 'Positive' },
  neutral:       { color: 'text-[#7a7a8a]',  bg: 'bg-[#38394e]/8  border border-[#38394e]/15', label: 'Neutral' },
  negative:      { color: 'text-[#c06a55]',  bg: 'bg-[#f0856d]/10 border border-[#f0856d]/30', label: 'Negative' },
  very_negative: { color: 'text-[#a0442e]',  bg: 'bg-[#f0856d]/20 border border-[#f0856d]/40', label: 'Very Negative' },
};

// ─── Main component ───────────────────────────────────────────────────────────

function MessageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [replies, setReplies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isOverridingLanguage, setIsOverridingLanguage] = useState(false);

  const conversationEndRef = useRef(null);

  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchMessage();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [replies]);

  const fetchMessage = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/messages/${id}`);
      if (response.data.success) {
        setMessage(response.data.message);
        setReplies(response.data.replies || []);
      }
    } catch (error) {
      console.error('Error fetching message:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !message) return;
    setIsSending(true);
    setSendError(null);
    try {
      const payload = { replyText, adminName: 'Admin' };

      const response = await axios.post(
        `${API_URL}/api/admin/messages/${message._id}/reply`,
        payload
      );
      if (response.data.success) {
        setReplyText('');
        await fetchMessage();
      } else {
        setSendError(response.data.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      setSendError(error.response?.data?.error || 'Failed to send reply');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleLanguageOverride = async (newLanguage) => {
    if (!message || newLanguage === message.originalLanguage) return;
    const langLabel = LANGUAGES.find(l => l.code === newLanguage)?.name || newLanguage;
    const confirmed = window.confirm(
      `Override language to "${langLabel}" and re-translate + re-analyse this message?`
    );
    if (!confirmed) return;
    setIsOverridingLanguage(true);
    setSendError(null);
    try {
      await axios.patch(`${API_URL}/api/admin/messages/${message._id}/language`, { language: newLanguage });
      await fetchMessage();
    } catch (error) {
      console.error('Error overriding language:', error);
      setSendError(error.response?.data?.error || 'Could not change the language. Please try again.');
    } finally {
      setIsOverridingLanguage(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatAgo = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const mins = Math.floor(ms / 60000);
    const hrs = Math.floor(ms / 3600000);
    const days = Math.floor(ms / 86400000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hrs  < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <MessageDetailSkeleton />
        </div>
      </DashboardLayout>
    );
  }

  // ── Not found state ────────────────────────────────────────────────────────

  if (!message) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h2 className="text-xl font-semibold text-foreground">Message not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">This message may have been deleted.</p>
          <button
            onClick={() => navigate('/messages')}
            className="mt-4 text-sm hover:underline"
            style={{ color: '#f0856d' }}
          >
            Back to messages
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const themes = getMessageThemes(message);
  const sentimentInfo = sentimentConfig[message.sentiment] || sentimentConfig.neutral;

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">

        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={fetchMessage}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* ── LEFT: Message details ── */}
          <div className="space-y-4">

            {/* Sender card */}
            <div className="rounded-lg border border-border bg-card shadow-sm p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="font-semibold text-foreground">{message.senderName || message.senderContact}</p>
                  {message.senderName && (
                    <p className="text-xs text-muted-foreground">{message.senderContact}</p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <ChannelIcon channel={message.channel} />
                    <span className="text-xs text-muted-foreground capitalize">{message.channel}</span>
                    {/* Voice note badge */}
                    {message.isVoiceNote && (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                        style={{ background: 'rgba(57,91,129,0.1)', border: '1px solid rgba(57,91,129,0.25)', color: '#395b81' }}>
                        <Mic className="h-3 w-3" />
                        Voice note
                      </span>
                    )}
                  </div>
                </div>
                <StatusBadge status={message.status} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Received</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(message.receivedAt)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Sentiment</p>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${sentimentInfo.bg} ${sentimentInfo.color}`}>
                    {sentimentInfo.label}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Themes</p>
                  <div className="flex flex-wrap gap-1">
                    {themes.map((theme) => (
                      <span
                        key={theme}
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                        style={{ background: getThemeColor(theme) }}
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
                {message.repliedAt && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Replied</p>
                    <p className="text-sm font-medium" style={{ color: '#547a84' }}>{formatDate(message.repliedAt)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Original message */}
            <div className="rounded-lg border border-border bg-card shadow-sm">
              <div className="p-6 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    {message.isVoiceNote ? 'Transcribed Message' : 'Original Message'}
                    {message.isVoiceNote && <Mic className="h-3.5 w-3.5 text-muted-foreground" />}
                    {message.metadata?.languageOverride && (
                      <span className="text-xs font-normal px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(240,133,109,0.12)', color: '#f0856d' }}>
                        overridden
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-1.5">
                    {isOverridingLanguage
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      : <Languages className="h-3.5 w-3.5 text-muted-foreground" />
                    }
                    <select
                      value={message.originalLanguage || ''}
                      onChange={e => handleLanguageOverride(e.target.value)}
                      disabled={isOverridingLanguage}
                      className="text-xs rounded-md border border-input bg-background px-2 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 cursor-pointer"
                      title="Override detected language"
                    >
                      {message.originalLanguage && !LANGUAGES.find(l => l.code === message.originalLanguage) && (
                        <option value={message.originalLanguage}>{message.originalLanguage} (detected)</option>
                      )}
                      {LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{message.originalText}</p>
              </div>
            </div>

            {/* Translated message */}
            {message.translatedText && message.translatedText !== message.originalText && (
              <div className="rounded-lg border border-border bg-card shadow-sm">
                <div className="p-6 pb-3">
                  <h3 className="text-sm font-semibold text-foreground">Translated Message (English)</h3>
                </div>
                <div className="px-6 pb-6">
                  <p className="text-sm leading-relaxed text-foreground">{message.translatedText}</p>
                </div>
              </div>
            )}

            {/* Processing errors */}
            {message.processingErrors?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <p className="text-sm font-semibold text-amber-800">
                    Processing errors ({message.processingErrors.length})
                  </p>
                </div>
                {message.processingErrors.map((e, i) => (
                  <p key={i} className="text-xs text-amber-700 pl-6">
                    <span className="font-semibold capitalize">{e.step}:</span> {e.error}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* ── RIGHT: Conversation thread ── */}
          <div className="flex flex-col">
            <div className="flex flex-1 flex-col rounded-lg border border-border bg-card shadow-sm">

              {/* Thread header */}
              <div className="flex items-center justify-between border-b border-border p-6">
                <div>
                  <h3 className="font-semibold text-foreground">Conversation</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {replies.length} repl{replies.length !== 1 ? 'ies' : 'y'} sent
                  </p>
                </div>
                {message.status === 'replied' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                    style={{ background: 'rgba(84,122,132,0.1)', border: '1px solid rgba(84,122,132,0.3)', color: '#547a84' }}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Replied
                  </span>
                )}
              </div>

              {/* Message bubbles */}
              <div
                className="flex-1 space-y-4 overflow-y-auto p-4"
                style={{ maxHeight: '420px' }}
              >
                {/* Original message — sender bubble (left) */}
                <div className="flex justify-start">
                  <div className="max-w-[80%]">
                    <p className="text-xs text-muted-foreground mb-1">
                      {message.senderContact} · {formatTime(message.receivedAt)}
                    </p>
                    <div className="rounded-2xl rounded-tl-sm bg-muted px-4 py-2.5">
                      {/* Voice note indicator in bubble */}
                      {message.isVoiceNote && (
                        <p className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                          <Mic className="h-3 w-3" />
                          Voice note — transcribed
                        </p>
                      )}
                      <p className="text-sm text-foreground leading-relaxed">
                        {message.translatedText || message.originalText}
                      </p>
                      {message.originalLanguage && message.originalLanguage !== 'en' && (
                        <p className="mt-1 text-xs text-muted-foreground italic">
                          Translated from {message.originalLanguage}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Admin replies — right aligned */}
                {replies.map((reply, idx) => {
                  const translation = reply.translations?.find(
                    t => t.messageId?.toString() === message._id?.toString()
                  );
                  const hasTranslation = translation?.text && translation.text !== reply.replyText;

                  return (
                    <div key={reply._id || idx} className="flex justify-end">
                      <div className="max-w-[80%]">
                        <p className="text-xs text-muted-foreground mb-1 text-right">
                          {reply.adminName} · {reply.sentAt ? formatAgo(reply.sentAt) : ''}
                        </p>
                        <div className="rounded-2xl rounded-tr-sm px-4 py-2.5" style={{ background: '#f0856d' }}>
                          <p className="text-sm leading-relaxed" style={{ color: 'white' }}>
                            {reply.replyText}
                          </p>
                          {hasTranslation && (
                            <>
                              <div className="my-2" style={{ borderTop: '1px solid rgba(255,255,255,0.25)' }} />
                              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                Sent in {translation.language}
                              </p>
                              <p className="mt-0.5 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {translation.text}
                              </p>
                            </>
                          )}
                          <p className="mt-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                            Sent to {reply.sentCount} recipient{reply.sentCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Sending indicator */}
                {isSending && (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5" style={{ background: 'rgba(240,133,109,0.4)' }}>
                      <p className="text-sm italic" style={{ color: 'white' }}>Sending…</p>
                    </div>
                  </div>
                )}

                <div ref={conversationEndRef} />
              </div>

              {/* Reply input */}
              <div className="border-t border-border p-4">
                {message.status === 'replied' && (
                  <p className="mb-3 flex items-center gap-1.5 text-xs font-medium" style={{ color: '#547a84' }}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Already replied — you can still send another message
                  </p>
                )}

                {/* SMS notice for web form phone submissions */}
                {message.channel === 'web' && message.metadata?.contactType === 'phone' && (
                  <div className="mb-3 flex items-center gap-2 rounded-md p-2.5 text-xs"
                    style={{ border: '1px solid rgba(57,91,129,0.3)', background: 'rgba(57,91,129,0.08)', color: '#395b81' }}>
                    <span className="font-semibold">Sending via SMS</span>
                    <span className="text-blue-600">·</span>
                    <span>{message.senderContact}</span>
                  </div>
                )}

                {/* Send / language-override failure */}
                {sendError && (
                  <div className="mb-3 flex items-start gap-2 rounded-md p-2.5 text-xs"
                    style={{ border: '1px solid rgba(240,133,109,0.4)', background: 'rgba(240,133,109,0.1)', color: '#a0442e' }}>
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" style={{ marginTop: 1 }} />
                    <span>{sendError}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <textarea
                    placeholder="Type your reply in English… it will be auto-translated for the recipient"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  />
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || isSending}
                    className="inline-flex h-10 items-center justify-center self-end rounded-md px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                    style={{ background: '#f0856d' }}
                    onMouseEnter={e => { if (replyText.trim() && !isSending) e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Press Enter to send · Shift+Enter for new line
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default MessageDetail;